/**
 * «Вакансии» — лента из Telegram-каналов и Talanto по фильтру; лучшие переносятся в «Мои отклики».
 */
import { FEED_LEVEL_LABELS, FORMAT_LABELS, fmtDate, httpUrl, vacancyLink, type FeedLevel } from '@jt/core';
import type { FeedCounts, FeedItem } from '@jt/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, get, post } from '../api/client';
import { keys, refreshSummaries } from '../api/hooks';
import { ConfirmButton, useUI } from '../components/overlays';
import { Empty, Icon, Logo, PageHead } from '../components/ui';

type Status = 'new' | 'saved' | 'hidden';
const feedKey = (status: Status, source: string) => ['feed', status, source] as const;

const ago = (iso: string) => {
  const m = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
  if (m < 60) return `${m || 1} мин назад`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} ч назад`;
  return fmtDate(iso.slice(0, 10));
};

function Facts({ i }: { i: FeedItem }) {
  const bits = [
    i.level && FEED_LEVEL_LABELS[i.level as FeedLevel],
    i.format && FORMAT_LABELS[i.format],
    i.location,
  ].filter(Boolean);
  return (
    <div className="feed-facts">
      {i.salary && <span className="feed-salary">{i.salary}</span>}
      {bits.map((b) => (
        <span key={String(b)} className="tag" title={String(b)}>
          {b}
        </span>
      ))}
    </div>
  );
}

const URL_SPLIT = /(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"»])/gi;

/** Текст поста с кликабельными ссылками. */
function Linked({ text }: { text: string }) {
  return (
    <>
      {text.split(URL_SPLIT).map((part, k) =>
        k % 2 ? (
          <a key={k} href={part} target="_blank" rel="noopener noreferrer">
            {part}
          </a>
        ) : (
          part
        ),
      )}
    </>
  );
}

function useFeedActions() {
  const ui = useUI();
  const qc = useQueryClient();
  const done = async () => {
    await qc.invalidateQueries({ queryKey: ['feed'] });
    refreshSummaries(qc);
  };
  return {
    save: async (i: FeedItem) => {
      try {
        const r = await post<{ applicationId: string; existed: boolean }>(`/feed/${i.id}/save`);
        await done();
        void qc.invalidateQueries({ queryKey: keys.apps() });
        ui.toast(
          r.existed ? 'Эта вакансия уже есть в «Моих откликах»' : 'В «Моих откликах», статус «Интересно»',
        );
        return r.applicationId;
      } catch (x) {
        ui.toast(x instanceof ApiError ? x.message : 'Не получилось');
        return null;
      }
    },
    hide: async (i: FeedItem) => {
      await post(`/feed/${i.id}/hide`);
      await done();
    },
    restore: async (i: FeedItem) => {
      await post(`/feed/${i.id}/restore`);
      await done();
    },
  };
}

function Detail({ i }: { i: FeedItem }) {
  const ui = useUI();
  const act = useFeedActions();
  return (
    <>
      <section className="sec">
        <div className="ch">
          <Logo name={i.company || i.sourceTitle} />
          <div className="ct">
            <b>{i.company || 'Компания не указана'}</b>
            <span className="meta">
              {i.sourceTitle} · {ago(i.postedAt)}
            </span>
          </div>
        </div>
        <Facts i={i} />
        {i.reason && <p className="muted sm">Почему в ленте: {i.reason}</p>}
        <div className="btnrow" style={{ marginTop: 8 }}>
          {i.status !== 'saved' ? (
            <button
              className="btn primary"
              onClick={async () => {
                const id = await act.save(i);
                if (id) ui.openApp(id);
              }}
            >
              <Icon name="plus" /> В мои отклики
            </button>
          ) : (
            i.applicationId && (
              <button className="btn" onClick={() => ui.openApp(i.applicationId!)}>
                Открыть в «Моих откликах»
              </button>
            )
          )}
          <a className="btn" href={httpUrl(i.url)} target="_blank" rel="noopener noreferrer">
            <Icon name="ext" size={15} /> {i.source === 'telegram' ? 'Открыть пост' : 'Открыть вакансию'}
          </a>
          {i.source === 'telegram' && vacancyLink(i.text) && (
            <a className="btn" href={httpUrl(vacancyLink(i.text))} target="_blank" rel="noopener noreferrer">
              <Icon name="ext" size={15} /> Описание вакансии
            </a>
          )}
          {i.status === 'new' && (
            <button
              className="btn ghost"
              onClick={async () => {
                await act.hide(i);
                ui.closeSheet();
                ui.toast('Скрыто — вернуть можно на вкладке «Скрытые»');
              }}
            >
              Скрыть
            </button>
          )}
          {i.status === 'hidden' && (
            <button
              className="btn"
              onClick={async () => {
                await act.restore(i);
                ui.closeSheet();
                ui.toast('Вакансия снова в «Новых»');
              }}
            >
              Вернуть
            </button>
          )}
        </div>
      </section>
      {i.also.length > 0 && (
        <section className="sec">
          <div className="meta">Та же вакансия ещё в:</div>
          <ul className="cdet">
            {i.also.map((a) => (
              <li key={a.url}>
                <Icon name="send" size={15} />
                <a href={httpUrl(a.url)} target="_blank" rel="noopener noreferrer">
                  {a.sourceTitle}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}
      {i.text && (
        <section className="sec">
          <pre className="feed-text">
            <Linked text={i.text} />
          </pre>
        </section>
      )}
    </>
  );
}

function FeedCard({ i }: { i: FeedItem }) {
  const ui = useUI();
  const act = useFeedActions();
  const open = () => ui.openSheet({ title: i.role || 'Вакансия', body: <Detail i={i} /> });
  return (
    <article
      className="card feed-card"
      tabIndex={0}
      aria-label={`${i.role || 'Вакансия'}, ${i.company || 'компания не указана'}`}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button,a')) return;
        open();
      }}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
          e.preventDefault();
          open();
        }
      }}
    >
      <div className="ch">
        <Logo name={i.company || i.sourceTitle} />
        <div className="ct">
          <b>{i.role || 'Вакансия'}</b>
          <span>{i.company || 'Компания не указана'}</span>
        </div>
      </div>
      <Facts i={i} />
      {i.text && <p className="feed-snippet">{i.text}</p>}
      <div className="feed-src">
        <span
          className={`tag ${i.source === 'talanto' ? 'ta' : i.source === 'himalayas' ? 'hm' : i.source === 'linkedin' ? 'li' : i.source === 'djinni' ? 'dj' : i.source === 'justjoin' ? 'jj' : 'tg'}`}
          title={i.sourceTitle}
        >
          {i.sourceTitle}
        </span>
        {i.also.length > 0 && <span className="muted sm">+{i.also.length} канал</span>}
        <span className="muted sm" style={{ marginLeft: 'auto' }}>
          {ago(i.postedAt)}
        </span>
      </div>
      <div className="btnrow">
        {i.status === 'new' && (
          <>
            <button className="btn sm primary" onClick={() => void act.save(i)}>
              <Icon name="plus" size={15} /> В мои отклики
            </button>
            <button className="btn sm ghost" onClick={() => void act.hide(i)}>
              Скрыть
            </button>
          </>
        )}
        {i.status === 'hidden' && (
          <button className="btn sm" onClick={() => void act.restore(i)}>
            Вернуть
          </button>
        )}
        {i.status === 'saved' && i.applicationId && (
          <button className="btn sm" onClick={() => ui.openApp(i.applicationId!)}>
            Открыть в откликах
          </button>
        )}
        <a
          className="btn sm ghost"
          href={httpUrl(i.url)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Открыть источник"
        >
          <Icon name="ext" size={15} />
        </a>
      </div>
    </article>
  );
}

/** Ссылка из Telegram «Открыть в трекере» — /feed?item=<id>: сразу открыть карточку. */
function useOpenFromLink() {
  const ui = useUI();
  const [params, setParams] = useSearchParams();
  const id = params.get('item');
  useEffect(() => {
    if (!id) return;
    setParams({}, { replace: true });
    get<FeedItem>(`/feed/${id}`)
      .then((i) => ui.openSheet({ title: i.role || 'Вакансия', body: <Detail i={i} /> }))
      .catch(() => ui.toast('Вакансии уже нет в ленте'));
  }, [id, ui, setParams]);
}

export function FeedPage() {
  const ui = useUI();
  const qc = useQueryClient();
  useOpenFromLink();
  const [status, setStatus] = useState<Status>('new');
  const [source, setSource] = useState('');
  const feed = useQuery({
    queryKey: feedKey(status, source),
    queryFn: () => get<FeedItem[]>(`/feed?status=${status}${source ? `&source=${source}` : ''}`),
    refetchInterval: 60_000,
  });
  const counts = useQuery({
    queryKey: ['feed', 'counts', source],
    queryFn: () => get<FeedCounts>(`/feed/counts${source ? `?source=${source}` : ''}`),
    refetchInterval: 60_000,
  });
  const list = feed.data ?? [];
  return (
    <>
      <PageHead
        title="Вакансии"
        sub="Новые вакансии из Telegram-каналов, Talanto, Himalayas, LinkedIn, Djinni и Just Join IT по вашему фильтру"
      >
        <button
          className="btn"
          onClick={async () => {
            try {
              await post('/feed/scan');
              ui.toast('Проверяю каналы — новые вакансии появятся через минуту');
              setTimeout(() => void qc.invalidateQueries({ queryKey: ['feed'] }), 20_000);
            } catch (x) {
              ui.toast(x instanceof ApiError ? x.message : 'Не получилось');
            }
          }}
        >
          <Icon name="restore" /> Проверить сейчас
        </button>
        <Link className="btn" to="/settings#feed">
          <Icon name="filter" /> Настройки ленты
        </Link>
      </PageHead>
      <div className="toolbar">
        <div className="seg" role="tablist" aria-label="Какие вакансии">
          {(
            [
              ['new', 'Новые'],
              ['saved', 'В откликах'],
              ['hidden', 'Скрытые'],
            ] as const
          ).map(([k, t]) => (
            <button key={k} role="tab" aria-selected={status === k} onClick={() => setStatus(k)}>
              {t}
              {counts.data && <span className="seg-n">{counts.data[k]}</span>}
            </button>
          ))}
        </div>
        <label className="selw">
          <Icon name="filter" size={18} />
          <select value={source} onChange={(e) => setSource(e.target.value)} aria-label="Источник">
            <option value="">Все источники</option>
            <option value="telegram">Telegram</option>
            <option value="talanto">Talanto</option>
            <option value="himalayas">Himalayas</option>
            <option value="linkedin">LinkedIn</option>
            <option value="djinni">Djinni</option>
            <option value="justjoin">Just Join IT</option>
          </select>
          <Icon name="chevD" />
        </label>
        {status === 'new' && list.length > 1 && (
          <ConfirmButton
            className="btn ghost"
            confirmText="Скрыть все новые?"
            onConfirm={async () => {
              const r = await post<{ count: number }>('/feed/hide-all');
              await qc.invalidateQueries({ queryKey: ['feed'] });
              refreshSummaries(qc);
              ui.toast(`Скрыто: ${r.count}`);
            }}
          >
            Скрыть все
          </ConfirmButton>
        )}
      </div>
      {feed.isPending ? null : list.length ? (
        <div className="arch-grid feed-grid">
          {list.map((i) => (
            <FeedCard key={i.id} i={i} />
          ))}
        </div>
      ) : (
        <Empty
          title={status === 'new' ? 'Новых вакансий пока нет' : 'Здесь пусто'}
          text={
            status === 'new'
              ? 'Подключите Telegram и добавьте каналы в настройках ленты — подходящие вакансии будут появляться здесь каждые 10 минут. Вакансии из Talanto приходят, если он подключён.'
              : 'Когда вы перенесёте вакансию в отклики или скроете её, она появится здесь.'
          }
        >
          <Link className="btn primary" to="/settings#feed">
            Настройки ленты
          </Link>
        </Empty>
      )}
    </>
  );
}
